import { forwardRef, useMemo, type ReactElement } from 'react';
import { useAdmin } from '../AdminContext';
import { paletteByKey, templateByKey } from './marketingOptions';
import { euro } from '../../core/format/currency';
import { fillTokens, marketingTokens } from './marketingRules';
import { PhotoDesign, ProductDesign, QuoteDesign, RoutineDesign, StoryDesign, TipDesign } from './SocialDesigns';
import { AftercareDesign, CardDesign, EmailDesign, FlyerDesign, GiftCardDesign, PosterDesign } from './PrintDesigns';
import type { DesignProps } from './designParts';
import type { MarketingDesign, TemplateKey } from '../../core/types/marketing';
const renderers: Record<TemplateKey, (props: DesignProps) => ReactElement> = {
  photo: PhotoDesign,
  quote: QuoteDesign,
  tip: TipDesign,
  product: ProductDesign,
  routine: RoutineDesign,
  story: StoryDesign,
  flyer: FlyerDesign,
  poster: PosterDesign,
  card: CardDesign,
  giftcard: GiftCardDesign,
  aftercare: AftercareDesign,
  email: EmailDesign
};

/**
 * Tekent één ontwerp op ware grootte (ontwerp-pixels). Tokens, productfoto's,
 * prijzen en contactgegevens komen live uit het concept in het beheer.
 */
export const DesignCanvas = forwardRef<HTMLDivElement, {
  design: MarketingDesign;
}>(function DesignCanvas({
  design
}, ref) {
  const {
    site,
    index
  } = useAdmin();
  const t = templateByKey(design.template);
  const Render = renderers[design.template];
  const props = useMemo<DesignProps>(() => {
    const tokens = marketingTokens(site);
    const f = (s: string) => fillTokens(s, tokens);
    const d: MarketingDesign = {
      ...design,
      eyebrow: f(design.eyebrow),
      headline: f(design.headline),
      accent: f(design.accent),
      body: f(design.body),
      cta: f(design.cta),
      points: design.points.map(f)
    };
    const product = design.productId ? index.productById(design.productId) : undefined;
    const r = design.routineId ? index.routineById(design.routineId) : undefined;
    const totals = r ? index.routineTotals(r) : undefined;
    return {
      d,
      p: paletteByKey(design.palette),
      w: t.w,
      h: t.h,
      city: site.salon.city,
      contact: [`${site.salon.street}, ${site.salon.postalCode} ${site.salon.locality}`, site.salon.phone],
      product,
      price: product ? euro(product.price) : undefined,
      routine: r && totals ? {
        name: r.name,
        items: totals.items,
        price: euro(totals.price)
      } : undefined
    };
  }, [design, site, index, t.w, t.h]);
  return <div ref={ref} style={{
    width: t.w,
    height: t.h
  }} data-id="element-2970" data-mp="12f2ujdeo364b">
      <Render {...props} data-id="element-2971" data-mp="xli2e1zr96d1" />
    </div>;
});